import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  ListItemText,
} from "@mui/material";
import "./registration-forms.scss";
import { getServiceCategories } from "../../../../../api/become-partner";
import { AddRounded, Close, DeleteOutline } from "@mui/icons-material";
import {
  AntSwitch,
  compactInputProps,
} from "../partner-registration-form.utils";
import NewPhoneNumberComponent from "../../../../../common/components/new-phone-input-component/new-phone-input-component";
import { getCategories } from "../../../../../api/admin/service-management";

type NeedType = "at_need" | "pre_need";
type Category = {
  id: string;
  name: string;
  needTypes: NeedType[];
  subServices: string[];
};
type ServiceRow = {
  categoryId: string;
  categoryName: string;
  subcategoryIds: string[];
};

type ServiceCategoryValue = {
  category: string;
  subcategories: string[];
};

const BusinessDetailsForm: React.FC<{ formik: any }> = ({ formik }) => {
  const pricingFileInputRef = React.useRef<HTMLInputElement>(null);
  const photosFileInputRef = React.useRef<HTMLInputElement>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [filterAtNeed, setFilterAtNeed] = useState(false);
  const [filterPreNeed, setFilterPreNeed] = useState(false);
  const [rowSubSelectValue, setRowSubSelectValue] = useState<
    Record<number, string>
  >({});
  const [categoryRows, setCategoryRows] = useState<any[]>([]);
  const [servicesListWithSelectedCategory, setServicesListWithSelectedCategory] = useState();
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const businessType = formik.values.businessType;
  useEffect(() => {
    // Reset need types and service categories when business type changes
    setFilterAtNeed(false);
    setFilterPreNeed(false);
    setCategoryRows([]);
    setRowSubSelectValue({});

    formik.setFieldValue('needTypes', []);
    formik.setFieldValue('serviceCategories', []);

    if (businessType === "Preneed Planner") {
      // Remove 'at_need' from formik values if present
      if (formik.values.needTypes?.includes('at_need')) {
        formik.setFieldValue(
          'needTypes',
          (formik.values.needTypes || []).filter((nt: string) => nt !== 'at_need')
        );
      }
    }
  }, [businessType]);
  const isSubSelected = (rowIdx: number, sub: string) =>
    categoryRows[rowIdx]?.subcategoryIds.includes(sub);

  const handleSingleFileSelect =
    (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] ?? null;
      formik.setFieldValue(fieldName, file);
      formik.setFieldTouched(fieldName, true, false);
    };

  const handleMultiFileSelect =
    (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files ? Array.from(e.target.files) : [];
      formik.setFieldValue(fieldName, files);
      formik.setFieldTouched(fieldName, true, false);
    };

  const hasNeedSelection = filterAtNeed || filterPreNeed;

  // useEffect(() => {
  //   let mounted = true;

  //   const load = async () => {
  //     if (!hasNeedSelection) {
  //       return;
  //     }

  //     try {
  //       setLoadingCats(true);
  //       const needParam =
  //         filterAtNeed && filterPreNeed
  //           ? "all"
  //           : filterAtNeed
  //             ? "at_need"
  //             : "pre_need";

  //       const res = await getServiceCategories(needParam);
  //       const json = res?.data?.data?.data;
  //       const raw = Array.isArray(json)
  //         ? json
  //         : (json?.data?.data ?? json?.data ?? []);
  //       const mapped: Category[] = (raw ?? []).map((it: any) => ({
  //         id: String(it.id),
  //         name: String(it.name ?? ""),
  //         needTypes: Array.isArray(it.needTypes) ? it.needTypes : [],
  //         subServices: Array.isArray(it.subServices) ? it.subServices : [],
  //       }));
  //       if (mounted) {
  //         setCategories(mapped);
  //         // setServiceRows([]);
  //       }
  //     } catch (e) {
  //       console.error("Failed to fetch services", e);
  //       if (mounted) setCategories([]);
  //     } finally {
  //       if (mounted) setLoadingCats(false);
  //     }
  //   };

  //   load();

  //   return () => {
  //     mounted = false;
  //   };
  // }, [filterAtNeed, filterPreNeed, hasNeedSelection]);

  useEffect(() => {
    if (!formik.dirty) {
      setCategoryRows([]);
      setRowSubSelectValue({});
      setFilterAtNeed(false);
      setFilterPreNeed(false);
    }
  }, [formik.dirty]);

  const availableCategoriesForRow = (rowIdx: number) => {
    // collecting all selected category IDs except for the current row
    const selectedOtherIds = categoryRows
      .filter((_, i) => i !== rowIdx)
      .map((r) => r.categoryId)
      .filter(Boolean);

    // base filtered categories by need type
    const base =
      (!filterAtNeed && !filterPreNeed) || (filterAtNeed && filterPreNeed)
        ? categories
        : filterAtNeed
          ? categories.filter((c) => c.needTypes.includes("at_need"))
          : categories.filter((c) => c.needTypes.includes("pre_need"));

    // excluding other selected categories
    return base.filter((c) => !selectedOtherIds.includes(c.id));
  };

  // useEffect to filter categoryRows when filters change
  // removing rows that no longer match active filters
  useEffect(() => {
    if (!hasNeedSelection) {
      setCategoryRows([]);
      setRowSubSelectValue({});
      return;
    }

    // building active needs array based on current filters
    const activeNeeds: NeedType[] = [];
    if (filterAtNeed) activeNeeds.push("at_need");
    if (filterPreNeed) activeNeeds.push("pre_need");

    // keep only those whose category's needTypes intersect with activeNeeds
    setCategoryRows((prev) =>
      prev.filter((row) => {
        const cat = categories.find((c) => c.id === row.categoryId);
        if (!cat) return false;

        // checking for not empty intersection
        const intersection = cat.needTypes.filter((need) =>
          activeNeeds.includes(need)
        );
        return intersection.length > 0;
      })
    );

    // clearing rowSubSelectValue for removed rows
    setRowSubSelectValue((prev) => {
      const next: Record<number, string> = {};
      categoryRows.forEach((_, idx) => {
        if (prev[idx] !== undefined) {
          next[idx] = prev[idx];
        }
      });
      return next;
    });
  }, [filterAtNeed, filterPreNeed, categories, hasNeedSelection]);

  useEffect(() => {
    if ((filterAtNeed === false || filterPreNeed === false) && hasNeedSelection === false) {
      setCategories([]);
      setCategoryRows([]);
    }
  }, [filterAtNeed, filterPreNeed]);

  const getCategoryName = (id: string) =>
    categories.find((c) => c.id === id)?.name ?? "";

  //service under category is now subcategory
  const getSubsForCategory = (id: string) =>
    categories.find((c) => c.id === id)?.services ?? [];

  useEffect(() => {
    if (!categories.length || categoryRows.length > 0) return;

    const initial = Array.isArray(formik.values?.serviceCategories)
      ? formik.values.serviceCategories
      : [];

    const rows: ServiceRow[] = initial
      .filter((it: any) => it && typeof it === "object")
      .slice(0, 10)
      .map((it: ServiceCategoryValue) => {
        const match = categories.find((c) => c.name === it.category);
        return {
          categoryId: match?.id ?? "",
          categoryName: it.category,
          subcategoryIds: Array.isArray(it.subcategories)
            ? it.subcategories.filter((s) => typeof s === "string")
            : [],
        };
      });

    if (rows.length) setCategoryRows(rows);
  }, [categories, formik.values?.serviceCategories, categoryRows.length]);

  useEffect(() => {
    const structured: ServiceCategoryValue[] = categoryRows
      .map((r) => {
        const catName = r.categoryName || getCategoryName(r.categoryId);
        return {
          category: catName || "",
          subcategories: r.subcategoryIds,
        };
      });

    const current = Array.isArray(formik.values?.serviceCategories)
      ? formik.values.serviceCategories
      : [];

    if (JSON.stringify(structured) !== JSON.stringify(current)) {
      formik.setFieldValue("serviceCategories", structured);
    }
  }, [categoryRows, categories]);

  const isRowValid = (r: ServiceRow) =>
    Boolean(r.categoryId) && r.subcategoryIds.length > 0;

  const allExistingRowsValid =
    categoryRows.length === 0 || categoryRows.every(isRowValid);

  const addDisabledReason = !hasNeedSelection
    ? "Select a need type"
    : loadingCats
      ? "Loading categories"
      : categories.length === 0
        ? "No service categories available"
        : categoryRows.length >= categories.length
          ? "All available service categories have been added"
          : !allExistingRowsValid
            ? "Complete the current service before adding another"
            : "";

  const isAddDisabled = Boolean(addDisabledReason);

  const addServiceRow = () => {
    if (isAddDisabled) return;

    //here subCategory means services under category
    setCategoryRows((prev) => [
      ...prev,
      { categoryId: "", categoryName: "", subcategoryIds: [] },
    ]);
  };

  useEffect(() => {
    const initial = Array.isArray(formik.values?.serviceCategories)
      ? formik.values.serviceCategories
      : [];
    if (hasNeedSelection && categoryRows.length === 0 && initial.length === 0) {
      setCategoryRows([
        { categoryId: "", categoryName: "", subcategoryIds: [] },
      ]);
    }
  }, [hasNeedSelection, categoryRows.length, formik.values?.serviceCategories]);

  const onRowCategoryChange = (rowIdx: number, categoryId: string) => {
    const name = getCategoryName(categoryId) || "";
    setCategoryRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
            categoryId,
            categoryName: name || r.categoryName,
            subcategoryIds: [],
          }
          : r
      )
    );

    formik.setFieldTouched("serviceCategories", true);
  };
  //here subName means service name under selected category
  const toggleRowSub = (rowIdx: number, subName: string) => {
    setCategoryRows((prev) =>
      prev.map((r, i) => {
        if (i !== rowIdx) return r;

        const exists = r.subcategoryIds.includes(subName);

        return {
          ...r,
          subcategoryIds: exists
            ? r.subcategoryIds.filter((s: any) => s !== subName)
            : [...r.subcategoryIds, subName],
        };
      })
    );

    formik.setFieldTouched("serviceCategories", true);
  };

  const getRowCategoryError = (row: ServiceRow) =>
    formik.touched.serviceCategories && !row.categoryId;

  const getRowSubCategoryError = (row: ServiceRow) =>
    formik.touched.serviceCategories && row.subcategoryIds.length === 0;

  const onRemoveRowChip = (rowIdx: number, subName: string) => {
    setCategoryRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
            ...r,
            subcategoryIds: r.subcategoryIds.filter((s) => s !== subName),
          }
          : r
      )
    );
    formik.setFieldTouched("serviceCategories", true);
  };

  const removeServiceRow = (rowIdx: number) => {
    setCategoryRows((prev) => prev.filter((_, i) => i !== rowIdx));
    setRowSubSelectValue((prev) => {
      const next: Record<number, string> = {};
      Object.keys(prev).forEach((kStr) => {
        const k = Number(kStr);
        if (k === rowIdx) return;
        const newK = k > rowIdx ? k - 1 : k;
        next[newK] = prev[k];
      });
      return next;
    });
  };

  function handleChange(event: {
    target: { name: string; value: string };
  }): void {
    const { name, value } = event.target;
    formik.setFieldValue(name, value);
    formik.setFieldTouched(name, true);
  }

  const onRowSubCategoriesChange = (rowIdx: number, value: string[]) => {
    setCategoryRows((prev) =>
      prev.map((r, i) =>
        i === rowIdx
          ? {
              ...r,
              subcategoryIds: value,
            }
          : r
      )
    );

    formik.setFieldTouched("serviceCategories", true);
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    formik.setFieldValue("isEmergencyServiceAvailable", checked);

    if (!checked) {
      setTimeout(() => {
        formik.setFieldValue("emergencyContactPhone", "");
        formik.setFieldTouched("emergencyContactPhone", false);
        formik.setFieldError("emergencyContactPhone", undefined);
      }, 0);
    }
  };

  const fetchAllCategories = async () => {
    try {
      const response = await getCategories({ type: "all" });
      const data = response?.data?.data?.data ?? [];
      setAllCategories(data);
    } catch (error) {
      console.error("Failed to fetch one or more services:", error);
    }
  };

  //to set categories based on need type selection
  const setCategoriesBasedOnNeedSelection = () => {
    if (!filterAtNeed && !filterPreNeed) return;
    const params = {
      type: filterAtNeed && filterPreNeed
        ? 'all'
        : filterAtNeed
          ? 'at_need'
          : 'pre_need',
    };
    const filteredCategories = allCategories.filter((category) => {
      const needTypes: NeedType[] = category?.needTypes || [];
      if (params.type === 'all') {
        return true;
      } else if (params.type === 'at_need') {
        return needTypes.includes('at_need');
      } else if (params.type === 'pre_need') {
        return needTypes.includes('pre_need');
      }
      return false;
    });
    setCategories(filteredCategories);

  }

  //fetch all categories on component mount
  useEffect(() => {
    fetchAllCategories();
  }, []);

  //set dropdown values only on filter change
  useEffect(() => {
    setCategoriesBasedOnNeedSelection()
  }, [filterAtNeed, filterPreNeed, hasNeedSelection])



  // const getSubCategoryName 
  const getServiceName = (categoryId: string, subCategoryId: string) => {
    const category = categories?.find((c) => c?.id === categoryId);
    if (!category) return "";

    const service = category?.services?.find((s: any) => s?.id === subCategoryId);
    return service ? service?.name : "";
  };

  return (
    <Box
      className="business-details-form"
      sx={{
        "& .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
        {
          borderWidth: "1px",
          borderColor: "rgba(0, 0, 0, 0.23)",
        },
        "& .MuiFormHelperText-root": {
          margin: 0,
          padding: 0,
          lineHeight: "16px",
        },
      }}
    >
      <Grid container spacing={1}>
        <Grid item xs={12} sm={12}>
          <Typography className="form-label">Business Description (max 500 characters limit)</Typography>
          <TextField
            fullWidth
            name="serviceDescription"
            placeholder="Enter description of services"
            multiline
            rows={3}
            value={formik.values.serviceDescription ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.serviceDescription &&
              Boolean(formik.errors.serviceDescription)
            }
            InputProps={{
              sx: {
                marginBottom: "8px",
              },
            }}
            inputProps={{ maxLength: 500 }}
          />
        </Grid>
      </Grid>
      <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
        <Box>
          <Typography className="form-label">
            Filter by Need Type<span>*</span>
          </Typography>
          <Stack direction="row" spacing={2}>
            <FormControlLabel
              disabled={businessType === "Preneed Planner"}
              control={
                <Checkbox
                  checked={filterAtNeed}
                  onChange={(e) => {
                    setFilterAtNeed(e.target.checked)
                    formik?.setFieldValue("needTypes", e.target.checked ? [...(formik.values.needTypes || []), 'at_need'] : (formik.values.needTypes || []).filter((nt: string) => nt !== 'at_need'))
                  }
                  }
                  size="small"
                  sx={{
                    borderRadius: "2px",
                    height: "18px",
                    width: "18px",
                    "&.Mui-checked": {
                      color: "#104451",
                    },
                  }}
                />
              }
              label="At-Need"
              sx={{
                cursor: businessType === "Preneed Planner"
                  ? "not-allowed !important"
                  : "pointer",
                gap: "10px",
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={filterPreNeed}
                  onChange={(e) => {
                    setFilterPreNeed(e.target.checked)
                    formik?.setFieldValue("needTypes", e.target.checked ? [...(formik.values.needTypes || []), 'pre_need'] : (formik.values.needTypes || []).filter((nt: string) => nt !== 'pre_need'))
                  }}
                  size="small"
                  sx={{
                    borderRadius: "2px",
                    height: "18px",
                    width: "18px",
                    "&.Mui-checked": {
                      color: "#104451",
                    },
                  }}
                />
              }
              label="Preneed"
              sx={{
                gap: "10px",
              }}
            />
          </Stack>
        </Box>
        <Button
          size="small"
          variant="contained"
          onClick={addServiceRow}
          disabled={isAddDisabled}
          startIcon={<AddRounded fontSize="small" />}
          sx={{
            ml: "auto",
            textTransform: "none",
            borderRadius: "8px",
            boxShadow: "none",
            minWidth: "140px",
            height: "40px",
            background: isAddDisabled ? "#9e9e9e" : "rgba(255, 237, 190, 1)",
            color: "#104145",
            fontWeight: 500,
            fontSize: "12px",
            "&:hover": {
              boxShadow: "none",
              background: "rgba(255, 237, 190, 1)",
            },
          }}
          title={addDisabledReason || "Add services"}
        >
          Add services
        </Button>
      </Box>
      {categoryRows.map((row, idx) => (
        <Box
          key={`${row.categoryId || "row"}-${idx}`}
          sx={{
            border: "1px solid rgba(21, 88, 93, 0.4)",
            borderRadius: "10px",
            backgroundColor: "rgba(224, 224, 224, 0.2)",
            p: 2,
            mb: 2,
            position: "relative",
          }}
        >
          {categoryRows.length > 1 &&
            formik.values.serviceCategories.length > 1 && (
              <IconButton
                aria-label="remove service"
                size="small"
                onClick={() => removeServiceRow(idx)}
                sx={{
                  position: "absolute",
                  top: 6,
                  right: 6,
                  color: "#104145",
                }}
              >
                <DeleteOutline fontSize="small" />
              </IconButton>
            )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography className="form-label">
                Service Categories<span>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                value={row.categoryId ?? ""}
                onChange={(e) => onRowCategoryChange(idx, e.target.value)}
                InputProps={compactInputProps}
                SelectProps={{ displayEmpty: true }}
                placeholder="Select Category"
                disabled={loadingCats || !hasNeedSelection}
                error={getRowCategoryError(row)}
                helperText={
                  getRowCategoryError(row) && "Service category is required"
                }
              >
                <MenuItem value="" disabled>
                  Select Category
                </MenuItem>
                {availableCategoriesForRow(idx)?.map((c) => (
                  <MenuItem key={c?.id} value={c?.id}>
                    {c?.name || ""}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Typography className="form-label">
                Services<span>*</span>
              </Typography>
              <TextField
                select
                fullWidth
                value={row.subcategoryIds ?? []}
                InputProps={compactInputProps}
                SelectProps={{
                  multiple: true,
                  displayEmpty: true,
                  renderValue: (selected: any) => {
                    const selectedValues = selected as string[];
                    if (!selectedValues?.length) return "Select Service";
                    return "Select Service";
                  },
                  onChange: (e) => {
                    const value = e.target.value as string | string[];
                    onRowSubCategoriesChange(
                      idx,
                      typeof value === "string" ? value.split(",") : value
                    );
                  },
                  MenuProps: {
                    disableAutoFocusItem: true,
                    PaperProps: {
                      style: {
                        maxHeight: 300,
                      },
                    },
                  },
                }}
                disabled={!row.categoryId || loadingCats || !hasNeedSelection}
                error={getRowSubCategoryError(row)}
                helperText={
                  getRowSubCategoryError(row) &&
                  "At least one service is required for selected category"
                }
              >
              {getSubsForCategory(row.categoryId).map((s: any) => {
                const selected = row.subcategoryIds.includes(s?.name);

                return (
                  <MenuItem
                    key={s?.name}
                    value={s?.name}
                    sx={{
                      backgroundColor: selected ? "#eff4fc" : "transparent",
                      "&:hover": {
                        backgroundColor: selected
                          ? "#eff4fc"
                          : "rgba(0,0,0,0.04)",
                      },
                    }}
                  >
                    <Checkbox size="small" checked={selected} />
                    <ListItemText primary={s?.name || ""} />
                  </MenuItem>
                );
              })}
            </TextField>
              {(filterAtNeed || filterPreNeed) && (
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{ mt: 1, flexWrap: "wrap", gap: 1 }}
                >
                  {row.subcategoryIds.map((sid) => (
                    <Chip
                      key={sid}
                      label={sid}
                      onDelete={() => onRemoveRowChip(idx, sid)}
                      size="small"
                      sx={{
                        background: "rgba(16, 65, 69, 0.3)",
                        color: "#104145",
                        borderRadius: "8px",
                      }}
                    />
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
          {/* {formik.touched.serviceCategories &&
            Boolean(formik.errors.serviceCategories) && (
              <Typography
                variant="body2"
                color="error"
                sx={{ fontSize: "12px", mt: 0.5 }}
              >
                {formik.errors.serviceCategories}
              </Typography>
            )} */}
        </Box>
      ))}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography className="form-label">
            Pricing Model / Packages
          </Typography>
          <input
            ref={pricingFileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleSingleFileSelect("pricingDocument")}
            onBlur={() => formik.setFieldTouched("pricingDocument", true)}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />

          <TextField
            fullWidth
            name="pricingDocument"
            placeholder="Upload pricing model / packages"
            value={formik.values.pricingDocument?.name || ""}
            onBlur={() => formik.setFieldTouched("pricingDocument", true)}
            InputProps={{
              ...compactInputProps,
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {formik.values.pricingDocument && (
                    <IconButton
                      aria-label="remove pricing document"
                      size="small"
                      onClick={() => {
                        formik.setFieldValue("pricingDocument", null);
                      }}
                      sx={{
                        height: 22,
                        width: 22,
                        color: "#104145",
                      }}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => pricingFileInputRef.current?.click()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "0 4px 4px 0",
                      boxShadow: "none",
                      width: "74px",
                      height: "39px",
                      background: "#DCDCDC",
                      color: "#104145",
                      fontWeight: 500,
                      fontSize: "12px",
                      cursor: "pointer",
                      "&:hover": { boxShadow: "none" },
                      right: "-13px",
                      top: "-1px",
                    }}
                  >
                    Upload
                  </Button>
                </InputAdornment>
              ),
            }}
            error={
              formik.touched.pricingDocument &&
              Boolean(formik.errors.pricingDocument)
            }
            helperText={
              formik.touched.pricingDocument && formik.errors.pricingDocument
            }
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <Typography className="form-label">
            Staff Strength / Capacity
          </Typography>
          <TextField
            fullWidth
            name="staffStrength"
            placeholder="Enter staff strength / capacity"
            InputProps={compactInputProps}
            value={formik.values.staffStrength ?? ""}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.staffStrength &&
              Boolean(formik.errors.staffStrength)
            }
            helperText={
              formik.touched.staffStrength && formik.errors.staffStrength
            }
            type="number"
            inputProps={{ min: 0, max: 99999, maxLength: 5 }}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Typography className="form-label">
            Upload Service Photos / Certificates
          </Typography>
          <input
            ref={photosFileInputRef}
            type="file"
            style={{ display: "none" }}
            onChange={handleMultiFileSelect("serviceFiles")}
            onBlur={() => formik.setFieldTouched("serviceFiles", true)}
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
          />

          <TextField
            fullWidth
            name="serviceFiles"
            placeholder="Upload service photos / certificates"
            value={
              Array.isArray(formik.values.serviceFiles)
                ? formik.values.serviceFiles.map((f: File) => f.name).join(", ")
                : ""
            }
            onBlur={() => formik.setFieldTouched("serviceFiles", true)}
            InputProps={{
              ...compactInputProps,
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  {Array.isArray(formik.values.serviceFiles) &&
                    formik.values.serviceFiles.length > 0 && (
                      <IconButton
                        aria-label="remove service files"
                        size="small"
                        onClick={() => {
                          formik.setFieldValue("serviceFiles", []);
                        }}
                        sx={{
                          height: 22,
                          width: 22,
                          color: "#104145",
                        }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    )}
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => photosFileInputRef.current?.click()}
                    sx={{
                      textTransform: "none",
                      borderRadius: "0 4px 4px 0",
                      boxShadow: "none",
                      width: "74px",
                      height: "39px",
                      background: "#DCDCDC",
                      color: "#104145",
                      fontWeight: 500,
                      fontSize: "12px",
                      cursor: "pointer",
                      "&:hover": { boxShadow: "none" },
                      right: "-13px",
                      top: "-1px",
                    }}
                  >
                    Upload
                  </Button>
                </InputAdornment>
              ),
            }}
            error={
              formik.touched.serviceFiles && Boolean(formik.errors.serviceFiles)
            }
            helperText={
              formik.touched.serviceFiles && formik.errors.serviceFiles
            }
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: "center", mt: 4 }}
          >
            <AntSwitch
              checked={formik.values.isEmergencyServiceAvailable}
              onChange={handleToggle}
              inputProps={{ "aria-label": "ant design" }}
              checkedColor="1890ff"
            />
            <Typography className="form-label">
              Emergency / 24x7 Service Availability
            </Typography>
          </Stack>
        </Grid>
      </Grid>
      {formik.values.isEmergencyServiceAvailable && (
        <Grid container spacing={2} sx={{ mt: 1, justifyContent: "flex-end" }}>
          <Grid item xs={12} sm={6}>
            <Typography className="form-label">
              Emergency Contact Phone
              <span>*</span>
            </Typography>
            <Box
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "4px !important",
                  height: "39px !important",
                  maxWidth: "100%",
                },
                "& .MuiInputBase-root": {
                  fontSize: "14px",
                  height: "39px !important",
                  padding: "10px 6px !important",
                },
                "& .MuiInputAdornment-root": {
                  backgroundColor: "#FFFFFF",
                  borderRadius: "6px 0 0 6px",
                },
                "& input": {
                  color: "#104145 !important",
                },
              }}
            >
             <NewPhoneNumberComponent
                value={formik.values.emergencyContactPhone}
                onChange={(mobileValue: string) => {
                  const cleanedValue = String(mobileValue || "").replace(/\D/g, "");
                  formik.setFieldValue("emergencyContactPhone", cleanedValue);
                  formik.setFieldTouched("emergencyContactPhone", true, false);
                }}
                countryCode={formik.values.emergencyContactPhoneCC}
                onCountryChange={(countryCodeValue: string) =>
                  formik.setFieldValue("emergencyContactPhoneCC", countryCodeValue)
                }
                placeholder="Enter emergency contact phone number"
                error={
                  formik.touched.emergencyContactPhone &&
                  Boolean(formik.errors.emergencyContactPhone)
                }
                helperText={
                  formik.touched.emergencyContactPhone &&
                  formik.errors.emergencyContactPhone
                }
                showHelper={true}
              />
            </Box>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default BusinessDetailsForm;
